import api from "../../../core/api/interceptors/axiosInterceptor";

/**
 * Get all sale orders for the current user/company with pagination
 * @param {object} params - Pagination parameters
 */
export const getSaleOrders = async (params = {}) => {
  try {
    const queryParams = {
      PageNumber: params.pageNumber || 1,
      PageSize: params.pageSize || 10,
      SearchTerm: params.searchTerm || undefined,
      OrderNumber: params.orderNumber || undefined,
      CustomerName: params.customerName || undefined,
      VATNumber: params.vatNumber || undefined,
      Status: params.status || undefined,
      PaymentStatus: params.paymentStatus || undefined,
      StartDate: params.startDate || undefined,
      EndDate: params.endDate || undefined,
      SortBy: params.sortBy || 'CreatedDate',
      SortOrder: params.sortOrder || 'desc',
    };
    
    // Remove undefined values
    Object.keys(queryParams).forEach(key => 
      queryParams[key] === undefined && delete queryParams[key]
    );
    
    const response = await api.get("SaleOrders", { params: queryParams });
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get a single sale order by ID
 */
export const getSaleOrderById = async (id) => {
  try {
    const response = await api.get(`SaleOrders/${id}`, { suppressErrorToast: true });
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get draft sale order by customer ID
 * Returns the draft sale order object if exists, otherwise returns null
 */
export const getDraftByCustomer = async (customerId) => {
  try {
    const response = await api.get(`SaleOrders/by-customer/${customerId}/draft`, { suppressErrorToast: true });
    return response.data;
  } catch (error) {
    // If no draft found (404), return null instead of throwing
    if (error.response?.status === 404) {
      return null;
    }

    throw error;
  }
};

/**
 * Get all sale orders by customer ID
 */
export const getSaleOrdersByCustomer = async (customerId) => {
  try {
    const response = await api.get(`SaleOrders/by-customer/${customerId}`);
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get the next sale order number
 */
export const getNextSaleOrderNumber = async () => {
  try {
    const response = await api.get("SaleOrders/next-number");
    return response.data;
  } catch (error) {

    return `SO-${Date.now() % 10000}`;
  }
};

/**
 * Add a new sale order (draft or finalized)
 */
export const addSaleOrder = async (saleOrderData) => {
  try {
    const response = await api.post("SaleOrders", saleOrderData, { suppressErrorToast: true });
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Update an existing sale order
 */
export const updateSaleOrder = async (id, saleOrderData) => {
  try {
    const response = await api.put(`SaleOrders/${id}`, saleOrderData, { suppressErrorToast: true });
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Add a product/line item to an existing sale order
 */
export const addSaleOrderLineItem = async (saleOrderId, lineItemData) => {
  try {
    const response = await api.post(`SaleOrders/${saleOrderId}/line-items`, lineItemData, { suppressErrorToast: true });
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Update a line item in a sale order
 */
export const updateSaleOrderLineItem = async (saleOrderId, lineItemId, lineItemData) => {
  try {
    const response = await api.put(`SaleOrders/${saleOrderId}/line-items/${lineItemId}`, lineItemData, { suppressErrorToast: true });
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Delete a line item from a sale order
 */
export const deleteSaleOrderLineItem = async (saleOrderId, lineItemId) => {
  try {
    const response = await api.delete(`SaleOrders/${saleOrderId}/line-items/${lineItemId}`, { suppressErrorToast: true });
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Delete a sale order
 */
export const deleteSaleOrder = async (id) => {
  try {
    const response = await api.delete(`SaleOrders/${id}`);
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Close a sale order (mark as closed/finalized)
 */
export const closeSaleOrder = async (id, data = {}) => {
  try {
    const response = await api.post(`SaleOrders/${id}/close`, data, { suppressErrorToast: true });
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Mark sale order as draft
 */
export const markSaleOrderAsDraft = async (id) => {
  try {
    const response = await api.post(`SaleOrders/${id}/mark-draft`);
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Convert sale order to invoice
 * @param {string} id - Sale order ID
 * @param {object} data - Optional data including lineItemIds array
 */
export const convertSaleOrderToInvoice = async (id, data = null) => {
  try {
    const response = await api.post(`SaleOrders/${id}/convert-to-invoice`, data);
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Record a payment for a sale order
 */
export const recordSaleOrderPayment = async (id, paymentData) => {
  try {
    const response = await api.post(`SaleOrders/${id}/payments`, paymentData);
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get payment history for a sale order
 */
export const getSaleOrderPayments = async (id) => {
  try {
    const response = await api.get(`SaleOrders/${id}/payments`);
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Send sale order via email
 */
export const sendSaleOrderEmail = async (id, emailData) => {
  try {
    const response = await api.post(`SaleOrders/${id}/send-email`, emailData);
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Search sale orders
 */
export const searchSaleOrders = async (searchTerm) => {
  try {
    const response = await api.get("SaleOrders/search", {
      params: { query: searchTerm },
    });
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get sale orders by status
 */
export const getSaleOrdersByStatus = async (status) => {
  try {
    const response = await api.get("SaleOrders/by-status", {
      params: { status },
    });
    return response.data;
  } catch (error) {

    throw error;
  }
};

/**
 * Get sale orders by payment status
 */
export const getSaleOrdersByPaymentStatus = async (paymentStatus) => {
  try {
    const response = await api.get("SaleOrders/by-payment-status", {
      params: { paymentStatus },
    });
    return response.data;
  } catch (error) {

    throw error;
  }
};
